import type { Distance, Point, SpatialRectangle } from "@pdfking/math"
import { Max } from "@pdfking/math"

import type { ResolvedSymmetry } from "#public"

import type { FillCounts, FillNodeData, FillSubjectsFlipped } from "../types/node"

/**
 * Python uses `XYMatrix(x, y)` as a tuple-like with element-wise
 * arithmetic. TS port models the same as `Point<number>`; arithmetic
 * is spelled out per-field since TS lacks NumPy-style operator
 * overloading. `len(xy)` in python = `xy.x * xy.y` for matrices.
 */
export const point_len = (p: Point<number>): number => p.x * p.y

/**
 * Mirrors `XYMatrix.rotate_fields()` — swaps x↔y. Python's name is
 * confusing; it's a tuple swap, not a geometric rotation.
 */
export const rotate_fields = (p: Point<Distance>): Point<Distance> => ({
  x: p.y,
  y: p.x,
})

/**
 * Mirrors Python `FillStrategy.__len__` (strategy.py:969-976):
 *   x_molt = int(self.symmetric_grow.x) + 1
 *   y_molt = int(self.symmetric_grow.y) + 1
 *   return (len(subjects) + len(subjects_flipped.x) + len(subjects_flipped.y))
 *          * (x_molt * y_molt)
 */
export const fill_len = (data: FillCounts): number => {
  const x_molt = (data.symmetric_grow.x ? 1 : 0) + 1
  const y_molt = (data.symmetric_grow.y ? 1 : 0) + 1
  return (
    (point_len(data.subjects) +
      point_len(data.subjects_flipped.x) +
      point_len(data.subjects_flipped.y)) *
    (x_molt * y_molt)
  )
}

/** Mirrors Python `FillStrategy.has_flip` (strategy.py:978-980). */
export const has_flip = (data: FillNodeData): boolean =>
  Boolean(point_len(data.subjects_flipped.x) || point_len(data.subjects_flipped.y))

/**
 * Mirrors Python `FillStrategy.x_symmetry` (strategy.py:982-990):
 *   if len(self) > 0:
 *       return symmetric_grow.x or (
 *           subjects.x % 2 == 0
 *           and not subjects_flipped.x
 *           and subjects_flipped.y.x % 2 == 0
 *       )
 *   return True
 */
export const x_symmetry = (data: FillNodeData): boolean => {
  if (fill_len(data) === 0) return true
  if (data.symmetric_grow.x) return true
  return (
    data.subjects.x % 2 === 0 &&
    !point_len(data.subjects_flipped.x) &&
    data.subjects_flipped.y.x % 2 === 0
  )
}

/** Mirrors Python `FillStrategy.y_symmetry` (strategy.py:992-1000). */
export const y_symmetry = (data: FillNodeData): boolean => {
  if (fill_len(data) === 0) return true
  if (data.symmetric_grow.y) return true
  return (
    data.subjects.y % 2 === 0 &&
    !point_len(data.subjects_flipped.y) &&
    data.subjects_flipped.x.y % 2 === 0
  )
}

/** Mirrors Python `FillStrategy.has_symmetry` (strategy.py:1002-1004). */
export const has_symmetry = (data: FillNodeData): boolean => x_symmetry(data) || y_symmetry(data)

/** Mirrors Python `FillStrategy.perfect_symmetry` (strategy.py:1006-1008). */
export const perfect_symmetry = (data: FillNodeData): boolean =>
  x_symmetry(data) && y_symmetry(data)

/**
 * Mirrors Python `FillStrategy.inner_total_size`
 * (strategy.py:1013-1018): inner + padding (per axis sum of left/right
 * and top/bottom).
 */
export const inner_total_size = (data: FillNodeData): Point<Distance> =>
  ({
    x: data.inner.width + data.padding.left + data.padding.right,
    y: data.inner.height + data.padding.top + data.padding.bottom,
  }) as Point<Distance>

/**
 * Mirrors Python `FillStrategy.subjects_flipped_total_size`
 * (strategy.py:1063-1068): per-axis ORXYMatrix where each axis carries
 * the flipped-subjects count multiplied by the rotated inner total.
 */
export const subjects_flipped_total_size = (data: FillNodeData): FillSubjectsFlipped => {
  const m = inner_total_size(data)
  const r = rotate_fields(m)
  return {
    x: {
      x: data.subjects_flipped.x.x * r.x,
      y: data.subjects_flipped.x.y * r.y,
    },
    y: {
      x: data.subjects_flipped.y.x * r.x,
      y: data.subjects_flipped.y.y * r.y,
    },
  }
}

/**
 * Mirrors Python `FillStrategy.plot_total_size` (strategy.py:1020-1032):
 *   m = inner_total_size
 *   s = symmetric_grow + 1   (booleans become 0/1, then +1)
 *   x1 = (subjects.x * m.x + subjects_flipped.x.x * m.y) * s.x
 *   x2 = subjects_flipped.y.x * m.y * s.x
 *   y1 = (subjects.y * m.y + subjects_flipped.y.y * m.x) * s.y
 *   y2 = subjects_flipped.x.y * m.x * s.y
 *   return XYMatrix(x=max(x1, x2), y=max(y1, y2))
 */
export const plot_total_size = (data: FillNodeData): Point<Distance> => {
  const m = inner_total_size(data)
  const sx = (data.symmetric_grow.x ? 1 : 0) + 1
  const sy = (data.symmetric_grow.y ? 1 : 0) + 1
  const x1 = (data.subjects.x * m.x + data.subjects_flipped.x.x * m.y) * sx
  const x2 = data.subjects_flipped.y.x * m.y * sx
  const y1 = (data.subjects.y * m.y + data.subjects_flipped.y.y * m.x) * sy
  const y2 = data.subjects_flipped.x.y * m.x * sy
  return { x: Max(x1, x2), y: Max(y1, y2) } as Point<Distance>
}

/**
 * Mirrors Python `FillStrategy.plot_origin` (strategy.py:1034-1044):
 *   XYMatrix(x=self.x1, y=self.y1)
 *   + (XYMatrix(x=self.width, y=self.height) - plot_total_size) / 2
 *   + XYMatrix(
 *       x=(margins.margin_left - margins.margin_right),
 *       y=(margins.margin_top - margins.margin_bottom),
 *   ) / 2
 *
 * Centers the plot inside `position`, offset by half the asymmetric
 * margin component.
 */
export const plot_origin = (
  data: FillNodeData,
  position: SpatialRectangle<Distance>
): Point<Distance> => {
  const total = plot_total_size(data)
  const free_x = position.width - total.x
  const free_y = position.height - total.y
  const margin_diff_x = data.security.left - data.security.right
  const margin_diff_y = data.security.top - data.security.bottom
  return {
    x: position.x + free_x / 2 + margin_diff_x / 2,
    y: position.y + free_y / 2 + margin_diff_y / 2,
  } as Point<Distance>
}

/**
 * Mirrors Python `FillStrategy.subjects_plot_diff` (strategy.py:1046-1057):
 *   s = subjects * (symmetric_grow + 1) * inner_total_size
 *   return XYMatrix(
 *       y=(subjects_flipped_total_size.x.y > s.y)
 *         and ((subjects_flipped_total_size.x.y - s.y) / 2) or D(),
 *       x=(subjects_flipped_total_size.y.x > s.x)
 *         and ((subjects_flipped_total_size.y.x - s.x) / 2) or D(),
 *   )
 */
export const subjects_plot_diff = (data: FillNodeData): Point<Distance> => {
  const m = inner_total_size(data)
  const sx_molt = (data.symmetric_grow.x ? 1 : 0) + 1
  const sy_molt = (data.symmetric_grow.y ? 1 : 0) + 1
  const s_x = data.subjects.x * sx_molt * m.x
  const s_y = data.subjects.y * sy_molt * m.y
  const flipped = subjects_flipped_total_size(data)
  return {
    x: flipped.y.x > s_x ? (flipped.y.x - s_x) / 2 : 0,
    y: flipped.x.y > s_y ? (flipped.x.y - s_y) / 2 : 0,
  } as Point<Distance>
}

/**
 * Mirrors Python `FillStrategy.get_used_symmetry`
 * (strategy.py:1076-1084):
 *   if not use_symmetry: return None
 *   if use_symmetry in ("x", "y"): return use_symmetry
 *   elif use_symmetry == True: return x_symmetry and "x" or "y"
 *   else: raise NotImplementedError
 */
export const get_used_symmetry = (data: FillNodeData): ResolvedSymmetry => {
  if (!data.use_symmetry) return null
  if (data.use_symmetry === "x" || data.use_symmetry === "y") {
    return data.use_symmetry
  }
  if (data.use_symmetry === "both") {
    return x_symmetry(data) ? "x" : "y"
  }
  throw new Error(`use_symmetry: ${data.use_symmetry}`)
}
